// Coastal current classifier for safety policy v2 (accelerated rollout Phase B, 1 October 2026;
// docs/handover/phase-2c.md, "Accelerated rollout, Phase B"). One rule for the strong-current rows
// of surf, bodyboarding and diving, and since the Phase C preparation (1 October 2026, "Accelerated
// rollout, Phase C preparation") of prone foil, downwind foil and the stand-up paddle profiles
// (Touring and the legacy sup, Surf, Downwind), in the engines' per-hour modules, the named paddle
// transport and the interim day summary the surf slice publishes at S2. Each sport keeps its own
// revision 1 amber and red speeds; the along-coast relief applies at beach breaks only, for every
// one of these sports except diving, downwind foil and the downwind paddle profile.
//
// Why: the current every coastal row reads is Open-Meteo's marine current, a cell of about 8 km
// whose nearest sea cell often lies offshore of the break. Where it carries a tidal stream it runs
// along the coast (the English Channel, the Strait of Gibraltar, the Pentland Firth), so revision 1's
// speed-only rule read red at those spots on nearly every day (H5: 7 of 40 spots, hourly current
// p90 1.0 m/s, p99 1.9 m/s), although at a beach break a stream that sets along the coast carries
// a surfer along the beach, not out to sea. The rule therefore judges direction where that holds
// (revised on 1 October 2026 after review: the first version relieved every spot type and diving
// too):
//
// - red: the current's seaward component (the part that sets away from the beach, out to sea)
//   is above the sport's red threshold, or the current is above the any-direction cap whichever
//   way it sets. The cap is ANY_DIRECTION_RED_CURRENT_MS (about 3.9 kn, faster than a surfer's
//   sprint paddle or any paddler's cruising speed) only where the along-coast relief applies: surf,
//   bodyboarding, prone foil and the Touring (with the legacy sup) and Surf paddle profiles at a beach break, where
//   a stream along the coast carries a rider or paddler along the beach to another landing, and
//   only the seaward part opposes the paddle back to shore. Every other case sets the cap at the
//   red threshold itself (alongCoastRelief false), so the speed alone reads red as revision 1 did:
//   - downwind foil and the downwind paddle profile (after review, 1 October 2026; the Phase C preparation first
//     relieved them too): a run is ridden in the stream itself, along a curated route, and the
//     stream often runs against the route while the run's own wind blows against the stream (wind
//     against tide: tarifa's levante, porthmeor's south-westerly). The relief reads neither the
//     route nor the wind, so it removed the only red on those days. The speed alone decides this
//     row; since the accelerated rollout Phase D, wind-against-tide.ts judges the stream against
//     the wind on a curated run beside it;
//   - diving: a diver's sustained swim is about 0.3 to 0.5 m/s, so nobody holds station against
//     0.8 m/s whichever way it sets, and for a boat dive the separation risk does not depend on
//     the direction relative to the beach (no curated dive entry type exists to tell a shore dive);
//   - reef, point and river-mouth breaks (and a spot whose type is not recorded): a stream along a
//     rocky coast carries a rider away from the one exit, along cliffs or past a headland;
// - amber: the current is above the sport's amber threshold in any direction (drift along the
//   coast, separation from the lineup or the exit: plan for slack water);
// - clear: below both.
//
// A missing direction or beach facing counts the whole speed as seaward, so a gap never lowers a
// current's level (design 2.5: a missing input never reads as safer). Directions: `towardsDeg` is
// where the current flows TO (Open-Meteo's ocean_current_direction, the oceanographic convention)
// and `beachFacingDeg` points from the beach out to sea, so a current towards the facing sets
// straight offshore.
//
// Pure and dependency-free: the route's interim rows (forecast-safety.ts, a declaration-only
// importer of the engines) and the engines' safety modules import it alike.

/** Red whatever the direction above this speed (m/s), where the along-coast relief applies. */
export const ANY_DIRECTION_RED_CURRENT_MS = 2.0;

export type CoastalCurrentThresholds = Readonly<{
  /** Amber when the speed (any direction) is above this, in m/s. */
  amberAboveMs: number;
  /** Red when the seaward component is above this, in m/s. */
  redSeawardAboveMs: number;
  /**
   * Whether a current along the coast is relieved up to ANY_DIRECTION_RED_CURRENT_MS (surf and
   * bodyboarding at a beach break). False or omitted: red when the speed in any direction is above
   * redSeawardAboveMs (see the header). Omitted means false, so a new caller never relaxes a red.
   */
  alongCoastRelief?: boolean;
}>;

/** The speed (m/s) above which a current is red whichever way it sets, under these thresholds. */
export function anyDirectionRedAboveMs(thresholds: CoastalCurrentThresholds): number {
  return thresholds.alongCoastRelief === true ? ANY_DIRECTION_RED_CURRENT_MS : thresholds.redSeawardAboveMs;
}

/**
 * Whether a surf or bodyboarding spot type takes the along-coast relief: a beach break only (the
 * catalogue's "beach" or "beach break"). Reef, point and river-mouth breaks, and a missing or
 * unrecognised type, do not (a missing input never reads as safer, design 2.5).
 */
export function beachBreakTakesAlongCoastRelief(spotType: string | null | undefined): boolean {
  if (typeof spotType !== "string") return false;
  return spotType.trim().toLowerCase().replace(/ break$/u, "").replaceAll(" ", "") === "beach";
}

/** The thresholds with the along-coast relief set for a spot type (surf and bodyboarding). */
export function currentThresholdsForSpot(thresholds: CoastalCurrentThresholds, spotType: string | null | undefined): CoastalCurrentThresholds {
  return { amberAboveMs: thresholds.amberAboveMs, redSeawardAboveMs: thresholds.redSeawardAboveMs, alongCoastRelief: beachBreakTakesAlongCoastRelief(spotType) };
}

export type CoastalCurrentReading = Readonly<{
  /** Speed in m/s; null when missing. */
  speedMs: number | null;
  /** Where the current flows to, in degrees; null when missing. */
  towardsDeg: number | null;
}>;

export type CoastalCurrentLevel = "red" | "amber" | "clear";

export type CoastalCurrentResult = Readonly<{
  level: CoastalCurrentLevel;
  speedMs: number;
  /** The seaward component in m/s (0 when the current sets onshore); the speed when unresolved. */
  seawardMs: number;
  /** Whether the direction and the facing were known, so the seaward component is resolved. */
  resolved: boolean;
  /** What set the level: the seaward component, the any-direction cap, the speed, or nothing. */
  reason: "seaward" | "any-direction" | "speed" | "below";
}>;

const finite = (value: number | null | undefined): value is number => typeof value === "number" && Number.isFinite(value);

/**
 * The seaward component of a current in m/s: speed times the cosine of the angle between where it
 * flows and the beach facing, floored at 0 (a current setting onshore carries nobody out to sea).
 * Without a direction or a facing the whole speed counts (see the header).
 */
export function seawardCurrentMs(speedMs: number, towardsDeg: number | null | undefined, beachFacingDeg: number | null | undefined): number {
  if (!finite(towardsDeg) || !finite(beachFacingDeg)) return speedMs;
  const component = speedMs * Math.cos(((towardsDeg - beachFacingDeg) * Math.PI) / 180);
  // Round away float noise so a current exactly along the coast reads 0, never 6e-17.
  return Math.max(0, Math.round(component * 1e9) / 1e9);
}

/**
 * The level from a current's speed and its seaward component (both m/s; `seawardMs` is the speed
 * itself when the direction or the facing is unknown, `resolved` false).
 */
export function classifySeawardCurrent(
  speedMs: number,
  seawardMs: number,
  thresholds: CoastalCurrentThresholds,
  resolved: boolean,
): CoastalCurrentResult {
  const base = { speedMs, seawardMs, resolved };
  if (seawardMs > thresholds.redSeawardAboveMs) return { ...base, level: "red", reason: "seaward" };
  if (speedMs > anyDirectionRedAboveMs(thresholds)) return { ...base, level: "red", reason: "any-direction" };
  if (speedMs > thresholds.amberAboveMs) return { ...base, level: "amber", reason: "speed" };
  return { ...base, level: "clear", reason: "below" };
}

/** The level of one current reading; null when its speed is missing or negative. */
export function classifyCoastalCurrent(
  reading: CoastalCurrentReading,
  beachFacingDeg: number | null | undefined,
  thresholds: CoastalCurrentThresholds,
): CoastalCurrentResult | null {
  const speed = reading.speedMs;
  if (!finite(speed) || speed < 0) return null;
  const resolved = finite(reading.towardsDeg) && finite(beachFacingDeg);
  return classifySeawardCurrent(speed, seawardCurrentMs(speed, reading.towardsDeg, beachFacingDeg), thresholds, resolved);
}

/**
 * The seaward component of one reading in m/s, or null when it cannot be resolved (a missing
 * speed, direction or beach facing), for the callers of classifySuppliedCurrent: null there counts
 * the whole speed as seaward.
 */
export function resolvedSeawardCurrentMs(
  speedMs: number | null | undefined,
  towardsDeg: number | null | undefined,
  beachFacingDeg: number | null | undefined,
): number | null {
  if (!finite(speedMs) || speedMs < 0 || !finite(towardsDeg) || !finite(beachFacingDeg)) return null;
  return seawardCurrentMs(speedMs, towardsDeg, beachFacingDeg);
}

/**
 * The level of a current whose caller supplies the seaward component (or null) and the relief: the
 * v2 leaf rows of prone foil, downwind foil and the paddle profiles (accelerated rollout Phase C
 * preparation). A missing or non-finite seaward component counts the whole speed as seaward
 * (unresolved), and a supplied one is clamped to between 0 and the speed, so a gap or a bad value
 * never lowers a level.
 */
export function classifySuppliedCurrent(
  speedMs: number,
  seawardMs: number | null | undefined,
  thresholds: CoastalCurrentThresholds,
): CoastalCurrentResult {
  const resolved = finite(seawardMs);
  return classifySeawardCurrent(speedMs, resolved ? Math.min(Math.max(seawardMs, 0), speedMs) : speedMs, thresholds, resolved);
}

/**
 * The peak speed and the peak seaward component over a set of readings (each from its own
 * reading: current is judged at its peak, design 2.7), so the level is the worst reading's; null
 * when no reading has a speed. `resolved` is true only when every reading with a speed was.
 */
export function peakCoastalCurrent(
  readings: readonly CoastalCurrentReading[],
  beachFacingDeg: number | null | undefined,
  thresholds: CoastalCurrentThresholds,
): CoastalCurrentResult | null {
  let speed: number | null = null;
  let seaward = 0;
  let resolved = true;
  for (const reading of readings) {
    const value = reading.speedMs;
    if (!finite(value) || value < 0) continue;
    speed = speed === null ? value : Math.max(speed, value);
    seaward = Math.max(seaward, seawardCurrentMs(value, reading.towardsDeg, beachFacingDeg));
    resolved &&= finite(reading.towardsDeg) && finite(beachFacingDeg);
  }
  return speed === null ? null : classifySeawardCurrent(speed, seaward, thresholds, resolved);
}

/**
 * The row label every sport shows for a flagged current: "Strong offshore current" when the
 * seaward component sets red, "Very strong current" for the any-direction cap (the speed alone,
 * where no relief applies), "Strong current" for amber (and for a clear result, which no caller
 * shows).
 */
export function coastalCurrentLabel(result: Pick<CoastalCurrentResult, "level" | "reason">): string {
  if (result.level === "red") return result.reason === "seaward" ? "Strong offshore current" : "Very strong current";
  return "Strong current";
}
